/**
 * `ImageDisplay` for auth-gated sources.
 *
 * A face crop lives behind `/api/v1/files/...`, which a bare `<img src>` cannot
 * load — the browser sends no bearer token and gets a 401. This wrapper runs
 * the source through `useAuthedImageUrl` (fetch + object URL, revoked on
 * unmount) and renders the plain display once the blob URL exists. Sources that
 * need no token (external URLs, `data:`/`blob:`) pass straight through.
 */
import { Loader2 } from 'lucide-react'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import { needsAuthedFetch } from '@/lib/authed-image-cache'
import { cn } from '@/utils/cn'
import { ImageDisplay, type ImageDisplayProps } from './ImageDisplay'

export function AuthedImageDisplay({ src, ...props }: ImageDisplayProps) {
  const authed = needsAuthedFetch(src)
  const blobUrl = useAuthedImageUrl(authed ? src : null)
  const resolved = authed ? blobUrl : src

  if (!resolved) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg bg-muted/20 py-6',
          props.className
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return <ImageDisplay {...props} src={resolved} />
}
